/**
 * Gallery Page
 *
 * Displays all user orders with filtering, lightbox viewing, and retry functionality.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - NO localStorage
 * - Real-time Firestore listener
 * - Lazy loading for images
 * - Service type filtering
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebaseConfig';
import { ServiceType, OrderDocument } from '@/shared/types';
import { Button, Card, LoadingSkeleton } from '@/components/ui';
import { OrderCard } from '@/components/OrderCard';
import { Lightbox } from '@/components/Lightbox';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/contexts/CreditsContext';
import { useToast } from '@/contexts/ToastContext';
import { useOrders } from '@/hooks/useOrders';
import { getServiceName, formatFileSize } from '@/utils/formatting';
import { downloadOrderResult } from '@/utils/download';

export function Gallery(): React.JSX.Element {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { credits, storageUsed, storageLimit } = useCredits();
  const { showToast } = useToast();

  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [lightboxOrder, setLightboxOrder] = useState<OrderDocument | null>(null);
  const [loadedDescription, setLoadedDescription] = useState<string | null>(null);

  // Fetch ALL orders (no filter passed) to get total count for header
  const { orders, loading, error } = useOrders(currentUser?.uid);

  // Filter orders client-side for the grid view
  const filteredOrders = useMemo(() => {
    if (!selectedService) return orders;
    return orders.filter((order) => order.serviceType === selectedService);
  }, [orders, selectedService]);

  /**
   * Handle view order in lightbox
   */
  const handleViewOrder = async (order: OrderDocument): Promise<void> => {
    if (order.serviceType === ServiceType.DESCRIPTION && order.currentResultUrl) {
      try {
        // For description, we show the original image (inputImageUrl) as the background
        // and load the text content from the result URL
        setLightboxOrder({
          ...order,
          // Ensure we show the input image as the main visual
          // The Lightbox component uses inputImageUrl when showOriginal is true,
          // or currentAttempt.resultUrl when false.
          // For Description, we want the background to be the house photo.
          // We map attempts to use inputImageUrl as resultUrl for display purposes
          attempts: order.attempts.map(a => ({
            ...a,
            resultUrl: order.inputImageUrl ?? null // Hack: show input image as result image
          }))
        });

        const response = await fetch(order.currentResultUrl);
        const text = await response.text();
        setLoadedDescription(text);
      } catch (error) {
        console.error('Failed to load description:', error);
        setLoadedDescription('Failed to load description text.');
        setLightboxOrder(order); // Fallback
      }
    } else {
      setLightboxOrder(order);
      setLoadedDescription(null);
    }
  };

  /**
   * Handle download order
   */
  const handleDownloadOrder = async (order: OrderDocument): Promise<void> => {
    if (!order.currentResultUrl) {
      showToast('No result available to download', 'error');
      return;
    }

    try {
      const latestAttempt = order.attempts[order.attempts.length - 1];
      await downloadOrderResult(
        order.currentResultUrl,
        order.serviceType,
        latestAttempt.attemptNumber
      );
      showToast('Download started!', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showToast('Download failed. Please try again.', 'error');
    }
  };

  /**
   * Handle retry order
   */
  const handleRetryOrder = async (order: OrderDocument): Promise<void> => {
    try {
      // Call retryOrder Cloud Function
      const retryOrderFunc = httpsCallable(functions, 'retryOrder');

      const result = await retryOrderFunc({ orderId: order.orderId });
      const { message, attemptNumber, maxAttempts } = result.data as {
        success: boolean;
        message: string;
        attemptNumber: number;
        maxAttempts: number;
      };

      console.log(`Retry successful: attempt ${attemptNumber} of ${maxAttempts}`);
      showToast(message, 'success');
    } catch (error) {
      console.error('Retry error:', error);

      // Handle specific error messages
      if (error instanceof Error) {
        showToast(error.message, 'error');
      } else {
        showToast('Retry failed. Please try again.', 'error');
      }
    }
  };

  /**
   * Handle delete order
   */
  const handleDeleteOrder = async (order: OrderDocument): Promise<void> => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this ${getServiceName(order.serviceType)} order? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      // Call deleteOrder Cloud Function
      const deleteOrderFunc = httpsCallable(functions, 'deleteOrder');

      const result = await deleteOrderFunc({ orderId: order.orderId });
      const { message, storageFreed } = result.data as {
        success: boolean;
        message: string;
        storageFreed: number;
      };

      const freedMB = (storageFreed / 1024 / 1024).toFixed(2);
      console.log(`Order deleted. Storage freed: ${freedMB}MB`);
      showToast(`${message} (${freedMB}MB freed)`, 'success');
    } catch (error) {
      console.error('Delete error:', error);

      // Handle specific error messages
      if (error instanceof Error) {
        showToast(error.message, 'error');
      } else {
        showToast('Delete failed. Please try again.', 'error');
      }
    }
  };

  /**
   * Handle retry from lightbox
   */
  const handleLightboxRetry = async (orderId: string): Promise<void> => {
    const orderToRetry = orders.find((o) => o.orderId === orderId);

    if (orderToRetry) {
      setLightboxOrder(null);
      await handleRetryOrder(orderToRetry);
    }
  };

  // Filter options
  const filterOptions = [
    { value: null, label: 'All Services' },
    { value: ServiceType.DAY_TO_DUSK, label: getServiceName(ServiceType.DAY_TO_DUSK) },
    { value: ServiceType.ITEM_REMOVAL, label: getServiceName(ServiceType.ITEM_REMOVAL) },
    { value: ServiceType.STAGING, label: getServiceName(ServiceType.STAGING) },
    { value: ServiceType.DESCRIPTION, label: getServiceName(ServiceType.DESCRIPTION) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">

            {/* Title and Stats */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">My Gallery</h1>

              {/* Divider for desktop */}
              <div className="hidden md:block h-6 w-px bg-gray-300 mx-2"></div>

              {/* Stats Pills */}
              <div className="flex flex-wrap gap-3 text-sm">
                <div className="flex items-center bg-green-50 text-green-800 px-3 py-1 rounded-full border border-green-100">
                  <span className="mr-2">💳</span>
                  <span className="font-bold">{credits}</span>
                  <span className="ml-1 opacity-75">credits</span>
                </div>

                <div className="flex items-center bg-blue-50 text-blue-800 px-3 py-1 rounded-full border border-blue-100">
                  <span className="mr-2">💾</span>
                  <span className="font-bold">{formatFileSize(storageUsed)}</span>
                  <span className="mx-1 opacity-50">/</span>
                  <span className="opacity-75">{formatFileSize(storageLimit)}</span>
                </div>

                <div className="flex items-center bg-purple-50 text-purple-800 px-3 py-1 rounded-full border border-purple-100">
                  <span className="mr-2">📦</span>
                  <span className="font-bold">{orders.length}</span>
                  <span className="ml-1 opacity-75">orders</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              <Button variant="secondary" onClick={() => navigate('/upload')}>
                Bulk Order
              </Button>
              <Button variant="primary" onClick={() => navigate('/dashboard')}>
                + New Order
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-6">
          <Card>
            <div className="p-4">
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">Filter by service:</label>
                <select
                  value={selectedService || ''}
                  onChange={(e) => setSelectedService(e.target.value as ServiceType || null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  {filterOptions.map((option) => (
                    <option key={option.label} value={option.value || ''}>
                      {option.label}
                    </option>
                  ))}
                </select>

                {selectedService && (
                  <Button variant="ghost" onClick={() => setSelectedService(null)}>
                    Clear Filter
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <LoadingSkeleton variant="rectangle" height="h-48" />
                <div className="p-4 space-y-3">
                  <LoadingSkeleton variant="text" />
                  <LoadingSkeleton variant="text" width="w-3/4" />
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card className="bg-red-50 border-2 border-red-500">
            <div className="p-6 text-center">
              <p className="text-red-700 font-semibold mb-2">Failed to load orders</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </Card>
        )}

        {/* Empty State */}
        {!loading && !error && filteredOrders.length === 0 && (
          <Card className="bg-gradient-to-br from-blue-100 to-green-100">
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">🖼️</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {selectedService ? 'No orders found for this service' : 'No orders yet'}
              </h2>
              <p className="text-gray-700 mb-6">
                {selectedService
                  ? 'Try selecting a different service filter or create a new order.'
                  : 'Create your first order to get started with AI-powered real estate image editing!'}
              </p>
              <Button variant="primary" onClick={() => navigate('/upload')}>
                Create Your First Order
              </Button>
            </div>
          </Card>
        )}

        {/* Orders Grid */}
        {!loading && filteredOrders.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.orderId}
                order={order}
                onView={handleViewOrder}
                onDownload={handleDownloadOrder}
                onRetry={handleRetryOrder}
                onDelete={handleDeleteOrder}
              />
            ))}
          </div>
        )}

        {/* Results Count */}
        {!loading && filteredOrders.length > 0 && (
          <div className="mt-6 text-center text-sm text-gray-600">
            Showing {filteredOrders.length} {selectedService ? getServiceName(selectedService) : ''} order
            {filteredOrders.length === 1 ? '' : 's'}
          </div>
        )}
      </main>

      {/* Lightbox */}
      {lightboxOrder && (
        <Lightbox
          order={lightboxOrder}
          isOpen={!!lightboxOrder}
          onClose={() => setLightboxOrder(null)}
          onRetry={handleLightboxRetry}
          descriptionText={loadedDescription}
        />
      )}
    </div>
  );
}